import { useCallback, useRef, useState } from 'react';

import type { FiltersValue } from '~features/dashboard-filters';
import { Filters } from '~features/dashboard-filters';

import type {
  FindPaymentsRequestParams,
  PaymentStatus,
} from '~entities/payment';
import { PaymentsExport } from '~entities/payment/ui/payments-export/payments-export';

import { DashboardTable } from '~shared/ui/dashboard-table';
import type { DashboardTableRef } from '~shared/ui/dashboard-table';
import { Loader } from '~shared/ui/loader';

import { columns } from '../lib/columns';
import { paymentStatusOptions } from '../lib/payments-status-options';
import { filtersToParams, sortToParams } from '../lib/search-params-converters';
import {
  colActionOptions,
  colSizingOptions,
  defaultSearchParams,
  defaultState,
} from '../lib/table-options';
import { usePaymentsTableDataQuery } from '../model/payments-table-queries';

export const PaymentsTable = () => {
  const [searchParams, setSearchParams] = useState<FindPaymentsRequestParams>();

  const { data, isError, isLoading } = usePaymentsTableDataQuery(searchParams);
  const tableRef = useRef<DashboardTableRef>(null);

  if (isError) {
    // we need to add default errors notifications
  }

  const handleFiltersChange = useCallback(
    (filters: FiltersValue<PaymentStatus>) => {
      tableRef.current?.setPage(0);
      setSearchParams((oldParams = defaultSearchParams) => ({
        ...oldParams,
        page: 0,
        filter: filtersToParams(filters),
      }));
    },
    []
  );

  return (
    <div className="flex min-h-[226px] flex-col gap-8">
      <Filters
        screen="payments"
        statusOptions={paymentStatusOptions}
        onFiltersChange={handleFiltersChange}
      />
      <div className="relative flex grow flex-col">
        {isLoading && <Loader />}
        {(data?.payments?.length ?? 0) > 0 && (
          <DashboardTable
            screen="payments"
            isPaginationEnabled
            data={data?.payments}
            totalAmount={data?.total}
            columns={columns}
            defaultState={defaultState}
            colSizingOptions={colSizingOptions}
            colActionOptions={colActionOptions}
            innerRef={tableRef}
            onStateChange={({ pagination, sort }) =>
              setSearchParams((oldParams) => {
                return {
                  ...oldParams,
                  page: pagination?.page ?? defaultSearchParams.page,
                  perPage:
                    pagination?.rowsPerPage ?? defaultSearchParams.perPage,
                  sort: sort && sortToParams(sort),
                };
              })
            }
          />
        )}
        {!isLoading && !data?.payments?.length && (
          <div
            className={`
              flex grow items-center justify-center text-center text-base
              text-gray-500
            `}
          >
            We didn&rsquo;t find anything
          </div>
        )}
      </div>
      <div className="flex justify-end">
        {!!searchParams && (
          <PaymentsExport
            params={searchParams}
            disabled={!data?.payments?.length}
          />
        )}
      </div>
    </div>
  );
};
